import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { hrApi } from '../api';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employeeId: string;
  employeeName?: string;
  /** Default date (YYYY-MM-DD) to prefill the entry on. */
  defaultDate?: string;
  onCreated: () => void;
}

/** Combine a YYYY-MM-DD date with a HH:mm time into an ISO string (local TZ). */
function toIso(date: string, time: string): string | null {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function ManualAttendanceDialog({ open, onOpenChange, employeeId, employeeName, defaultDate, onCreated }: Props) {
  const [date, setDate] = React.useState('');
  const [inTime, setInTime] = React.useState('09:00');
  const [outTime, setOutTime] = React.useState('17:00');
  const [stillOpen, setStillOpen] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setDate(defaultDate ?? new Date().toISOString().slice(0, 10));
      setInTime('09:00'); setOutTime('17:00'); setStillOpen(false); setNotes('');
    }
  }, [open, defaultDate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const clockIn = toIso(date, inTime);
    if (!clockIn) { toast.error('Pick a valid date and clock-in time'); return; }
    let clockOut: string | null = null;
    if (!stillOpen) {
      clockOut = toIso(date, outTime);
      if (!clockOut) { toast.error('Pick a valid clock-out time'); return; }
    }
    setBusy(true);
    try {
      await hrApi.createManualSession({
        employee_id: employeeId,
        clock_in_at: clockIn,
        clock_out_at: clockOut,
        notes: notes || null,
      });
      toast.success('Attendance entry added');
      onCreated();
    } catch (err: any) {
      toast.error('Could not add entry', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add attendance entry{employeeName ? ` — ${employeeName}` : ''}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Clock in</Label>
              <Input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>Clock out</Label>
              <Input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} disabled={stillOpen} />
            </div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer text-[13px] text-muted-foreground">
            <input
              type="checkbox"
              checked={stillOpen}
              onChange={(e) => setStillOpen(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-saffron"
            />
            Leave session open (no clock-out yet)
          </label>
          <div className="grid gap-1.5">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. Forgot to clock in via the app" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Add entry'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
